import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';

/**
 * Device Detection Service
 * Detects device types based on Tailwind CSS breakpoints and provides reactive properties
 *
 * Tailwind Breakpoints:
 * - sm: 640px
 * - md: 768px
 * - lg: 1024px
 * - xl: 1280px
 * - 2xl: 1536px
 */
export default class DeviceDetectionService extends Service {
  @tracked _currentWidth = 0;
  @tracked _currentHeight = 0;

  // Breakpoint values matching Tailwind CSS
  breakpoints = {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1536
  };

  // Height breakpoints (customizable)
  heightBreakpoints = {
    xs: 600,    // Very short screens (old phones landscape)
    sm: 700,    // Short screens
    md: 800,    // Medium height
    lg: 900,    // Tall screens
    xl: 1080,   // Very tall screens (standard desktop)
  };

  constructor() {
    super(...arguments);
    this._initializeDetection();
  }

  /**
   * Initialize device detection and set up resize listener
   */
  _initializeDetection() {
    if (typeof window !== 'undefined') {
      this._updateDimensions();
      window.addEventListener('resize', this._handleResize);
    }
  }

  /**
   * Handle window resize events
   */
  @action
  _handleResize() {
    this._updateDimensions();
  }

  /**
   * Update the current width and height
   */
  _updateDimensions() {
    if (typeof window !== 'undefined') {
      this._currentWidth = window.innerWidth;
      this._currentHeight = window.innerHeight;
    }
  }

  /**
   * Get current window width
   * @returns {number} Current window width in pixels
   */
  get currentWidth() {
    return this._currentWidth;
  }

  /**
   * Get current window height
   * @returns {number} Current window height in pixels
   */
  get currentHeight() {
    return this._currentHeight;
  }

  /**
   * Get viewport dimensions
   * @returns {{width: number, height: number}}
   */
  get viewportSize() {
    return {
      width: this._currentWidth,
      height: this._currentHeight
    };
  }

  /**
   * Check if current device is a phone
   * Phone: < 768px (below md breakpoint)
   * @returns {boolean}
   */
  get isPhone() {
    return this._currentWidth < this.breakpoints.md;
  }

  /**
   * Check if current device is a tablet
   * Tablet: 768px - 1023px (md to lg-1)
   * @returns {boolean}
   */
  get isTablet() {
    return this._currentWidth >= this.breakpoints.md &&
           this._currentWidth < this.breakpoints.lg;
  }

  /**
   * Check if current device is desktop
   * Desktop: >= 1024px (lg breakpoint and above)
   * @returns {boolean}
   */
  get isDesktop() {
    return this._currentWidth >= this.breakpoints.lg;
  }

  /**
   * Get current device type as string
   * @returns {'phone' | 'tablet' | 'desktop'}
   */
  get deviceType() {
    if (this.isPhone) return 'phone';
    if (this.isTablet) return 'tablet';
    return 'desktop';
  }

  /**
   * Check if viewport matches a specific Tailwind breakpoint
   * @param {string} breakpoint - Tailwind breakpoint name ('sm', 'md', 'lg', 'xl', '2xl')
   * @returns {boolean}
   */
  matchesBreakpoint(breakpoint) {
    const minWidth = this.breakpoints[breakpoint];
    if (!minWidth) {
      return false;
    }
    return this._currentWidth >= minWidth;
  }

  /**
   * Check if viewport is between two breakpoints
   * @param {string} minBreakpoint - Minimum breakpoint (inclusive)
   * @param {string} maxBreakpoint - Maximum breakpoint (exclusive)
   * @returns {boolean}
   */
  isBetweenBreakpoints(minBreakpoint, maxBreakpoint) {
    const minWidth = this.breakpoints[minBreakpoint] || 0;
    const maxWidth = this.breakpoints[maxBreakpoint] || Infinity;
    return this._currentWidth >= minWidth && this._currentWidth < maxWidth;
  }

  /**
   * Get all current active breakpoints
   * @returns {string[]} Array of active breakpoint names
   */
  get activeBreakpoints() {
    return Object.keys(this.breakpoints).filter(bp =>
      this.matchesBreakpoint(bp)
    );
  }

  /**
   * Get the current breakpoint tier
   * @returns {string} Current breakpoint name
   */
  get currentBreakpoint() {
    if (this._currentWidth >= this.breakpoints['2xl']) return '2xl';
    if (this._currentWidth >= this.breakpoints.xl) return 'xl';
    if (this._currentWidth >= this.breakpoints.lg) return 'lg';
    if (this._currentWidth >= this.breakpoints.md) return 'md';
    if (this._currentWidth >= this.breakpoints.sm) return 'sm';
    return 'xs';
  }

  /**
   * Check if device supports touch
   * @returns {boolean}
   */
  get isTouchDevice() {
    if (typeof window === 'undefined') return false;
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  /**
   * Check if device is likely mobile (combines size and touch detection)
   * @returns {boolean}
   */
  get isMobile() {
    return this.isPhone && this.isTouchDevice;
  }

  // ==================== HEIGHT DETECTION ====================

  /**
   * Check if viewport height matches a specific height breakpoint
   * @param {string} breakpoint - Height breakpoint name ('xs', 'sm', 'md', 'lg', 'xl')
   * @returns {boolean}
   */
  matchesHeightBreakpoint(breakpoint) {
    const height = this.heightBreakpoints[breakpoint];
    return height ? this._currentHeight >= height : false;
  }

  /**
   * Check if viewport height is between two height breakpoints
   * @param {string} minBreakpoint - Minimum height breakpoint (inclusive)
   * @param {string} maxBreakpoint - Maximum height breakpoint (exclusive)
   * @returns {boolean}
   */
  isBetweenHeightBreakpoints(minBreakpoint, maxBreakpoint) {
    const minHeight = this.heightBreakpoints[minBreakpoint] || 0;
    const maxHeight = this.heightBreakpoints[maxBreakpoint] || Infinity;
    return this._currentHeight >= minHeight && this._currentHeight < maxHeight;
  }

  /**
   * Get the current height breakpoint tier
   * @returns {string} Current height breakpoint name
   */
  get currentHeightBreakpoint() {
    if (this._currentHeight >= this.heightBreakpoints.xl) return 'xl';
    if (this._currentHeight >= this.heightBreakpoints.lg) return 'lg';
    if (this._currentHeight >= this.heightBreakpoints.md) return 'md';
    if (this._currentHeight >= this.heightBreakpoints.sm) return 'sm';
    if (this._currentHeight >= this.heightBreakpoints.xs) return 'xs';
    return 'xxs';
  }

  /**
   * Check if viewport is very short (< 600px)
   * @returns {boolean}
   */
  get isShortViewport() {
    return this._currentHeight < this.heightBreakpoints.xs;
  }

  /**
   * Check if viewport is medium height (700px - 899px)
   * @returns {boolean}
   */
  get isMediumViewport() {
    return this._currentHeight >= this.heightBreakpoints.sm &&
           this._currentHeight < this.heightBreakpoints.lg;
  }

  /**
   * Check if viewport is tall (>= 900px)
   * @returns {boolean}
   */
  get isTallViewport() {
    return this._currentHeight >= this.heightBreakpoints.lg;
  }

  /**
   * Get viewport orientation
   * @returns {'portrait' | 'landscape' | 'square'}
   */
  get orientation() {
    if (this._currentHeight > this._currentWidth) return 'portrait';
    if (this._currentWidth > this._currentHeight) return 'landscape';
    return 'square';
  }

  /**
   * Check if viewport is in portrait mode
   * @returns {boolean}
   */
  get isPortrait() {
    return this.orientation === 'portrait';
  }

  /**
   * Check if viewport is in landscape mode
   * @returns {boolean}
   */
  get isLandscape() {
    return this.orientation === 'landscape';
  }

  /**
   * Detect if system is capable of ultra tier (48k stars)
   * Ultra tier requires: 8+ cores AND 8GB+ memory AND desktop
   * @returns {boolean}
   */
  detectUltraCapable() {
    if (typeof window === 'undefined') return false;

    // Must be desktop
    if (!this.isDesktop) return false;

    // Check hardware concurrency (CPU cores)
    const cores = navigator.hardwareConcurrency || 2;

    // Check device memory (if available)
    const memory = navigator.deviceMemory || 4;

    // Ultra tier requires: 8+ cores AND 8GB+ memory
    return cores >= 8 && memory >= 8;
  }

  /**
   * Detect performance quality tier for canvas rendering
   * Returns 'ultra', 'high', 'medium', or 'low' based on device capabilities
   * @returns {'ultra' | 'high' | 'medium' | 'low'}
   */
  detectQualityTier() {
    if (typeof window === 'undefined') return 'medium';

    // Respect user preference first
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return 'low';
    }

    // Check if system is ultra-capable
    if (this.detectUltraCapable()) {
      return 'ultra';
    }

    // Check hardware concurrency (CPU cores)
    const cores = navigator.hardwareConcurrency || 2;

    // Check device memory (if available)
    const memory = navigator.deviceMemory || 4;

    // Mobile detection - phones get low quality
    if (this.isPhone) return 'low';

    // Tablets get medium quality
    if (this.isTablet) return 'medium';

    // Desktop with limited resources
    if (cores <= 2 || memory <= 2) return 'low';
    if (cores <= 4 || memory <= 4) return 'medium';

    // High-end desktop
    return 'high';
  }

  /**
   * Cleanup when service is destroyed
   */
  willDestroy() {
    super.willDestroy();
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this._handleResize);
    }
  }
}
